import { _js } from '@ifixit/utils/localize';
import { COLOR_BULLETS, ICON_BULLETS } from 'Guide/step_bullets';

// The palette (colors, icons, and their render helpers) is the canonical
// step-bullet source shared with the embed reader and translation editor; it
// lives in Guide/step_bullets. Re-exported here so existing `./bullets` imports
// keep resolving. The label lookup and cycle-shortcut logic below are
// editor-only, so they stay in this module.
export {
   bulletColor,
   bulletSvg,
   isIconBullet,
   COLOR_BULLETS,
   ICON_BULLETS,
} from 'Guide/step_bullets';

// Human labels for the bullets: raw enum names ("light_blue") read like
// debugging output in a screen reader and in tooltips, and bullet color is
// meaningful content — it pairs a line with the same-colored marker drawn
// on the step image.
const BULLET_LABELS: Record<string, () => string> = {
   black: () => _js('Black'),
   red: () => _js('Red'),
   orange: () => _js('Orange'),
   yellow: () => _js('Yellow'),
   green: () => _js('Green'),
   light_blue: () => _js('Light blue'),
   blue: () => _js('Blue'),
   violet: () => _js('Violet'),
   icon_caution: () => _js('Caution'),
   icon_note: () => _js('Note'),
   icon_reminder: () => _js('Reminder'),
};

export function bulletLabel(name: string): string {
   return BULLET_LABELS[name]?.() ?? name;
}

// The shortcut's cycle is every bullet in one ring: black (the "no color"
// stop) then the colors in the ROYGBV order authors mark up images in, then
// the three icon bullets, then back to black. COLOR_RAMP is the colors alone
// (no black, no icons) — the sequence the "continue from the line above"
// walk follows.
const CYCLE: readonly string[] = [
   ...COLOR_BULLETS.map(bullet => bullet.name),
   ...ICON_BULLETS.map(bullet => bullet.name),
];
const COLOR_RAMP: readonly string[] = COLOR_BULLETS.filter(bullet => bullet.name !== 'black').map(
   bullet => bullet.name
);

/**
 * The bullet the cycle shortcut moves `lines[index]` to. Forward from black
 * continues the color ramp from the nearest colored line above, so one press
 * per line walks ROYGBV down the step; forward from a color or icon steps
 * along the ring (violet into the icons, the last icon back to black).
 * Backward retraces the same ring.
 */
export function cycledBullet(
   lines: readonly { bullet: string }[],
   index: number,
   direction: 1 | -1
): string {
   const at = CYCLE.indexOf(lines[index].bullet);
   if (direction === -1) {
      // An unknown bullet re-enters the ring at black.
      const from = at === -1 ? 0 : at;
      return CYCLE[(from - 1 + CYCLE.length) % CYCLE.length];
   }
   if (at > 0) {
      return CYCLE[(at + 1) % CYCLE.length];
   }
   for (let above = index - 1; above >= 0; above--) {
      const ramp = COLOR_RAMP.indexOf(lines[above].bullet);
      if (ramp !== -1) {
         // Skips black when the line above is violet: continuing the ramp
         // means the next marker color, not "no color".
         return COLOR_RAMP[(ramp + 1) % COLOR_RAMP.length];
      }
   }
   return COLOR_RAMP[0];
}
